import React, { useEffect, useState } from 'react';
import { Nav } from 'rsuite5';
import usePrefix from '@/hooks/usePrefix';
import _ from 'lodash';
import useRouter from '@/hooks/useRouter';
import history, { replace } from '@/utils/history';
import { RsInput as Input } from '../Bicester';
import { IconButton } from 'rsuite';
import Image from '../Bicester/Image';
import { SITE_SERVICE_TABS } from '@/views/SiteService/constants';
import { MAKE_SERVICE } from '@/views/SubscribeService/constants';

export interface NavTab {
  key: string;
  label?: string | React.ReactElement;
  url?: string;
}

interface ToolbarContextValue {
  searchWord?: string;
  setSearchWord?: (value: string) => void;
}

const ToolbarContext = React.createContext<ToolbarContextValue>(null);

const BicesterToolbar = ({
  className,
  children,
}: {
  className?: string;
  children: React.ReactNode;
}) => {
  const [word, setWord] = useState('');

  return (
    <ToolbarContext.Provider
      value={{
        searchWord: word,
        setSearchWord: setWord,
      }}
    >
      <div className={`bicester-toolbar ${className || ''}`}>{children}</div>
    </ToolbarContext.Provider>
  );
};

const NavBar = ({
  data,
  activeTab,
  isActiveDefault = true,
  onSelect,
  isPhoneWord = false,
}: {
  data: NavTab[];
  activeTab?: string;
  isActiveDefault?: boolean; // 是否自动选中，false则所有选项默认都不高亮
  onSelect?: (eventKey: string, event?: React.SyntheticEvent<Element, Event>) => void;
  /** 搜索框输入的搜索词是否是手机号，非手机号则切换navBar时清空 */
  isPhoneWord?: boolean;
}) => {
  const { setSearchWord } = React.useContext(ToolbarContext);
  const { pathname, query } = useRouter();
  const { prefix, classname } = usePrefix('bicester-toolbar-navbar');

  const [pathName, setPathName] = useState('');
  const [active, setActive] = useState('');

  const getDefaultTab = (navTab) => {
    return _.get(navTab, '[0].key', '');
  };

  const getCurrentTab = (pathname: string, navTabs) => {
    if (pathname.includes('/site-service/l-and-f')) {
      return SITE_SERVICE_TABS.L_AND_F;
    } else if (pathname.includes('/site-service/record')) {
      return SITE_SERVICE_TABS.RECORD;
    } else if (pathname.includes('/subscribe-service/log')) {
      return MAKE_SERVICE.LOG;
    }
    return navTabs.find((nav) => pathname?.endsWith(nav.url))?.key ?? '';
  };

  useEffect(() => {
    setActive(isActiveDefault ? getCurrentTab(pathname, data) || getDefaultTab(data) : '');
  }, []);

  history.listen((location) => {
    const { pathname } = location;
    if (pathName !== pathname) {
      setPathName(pathname);
      setActive(getCurrentTab(pathname, data) || getDefaultTab(data));
    }
  });

  return (
    <div className={classname}>
      <Nav className={prefix('nav')}>
        {data.map((navTab, index) => {
          const { key, url } = navTab;
          return (
            <div className={prefix('item-wrap')} key={key}>
              <Nav.Item
                className={prefix('item')}
                active={isActiveDefault && active === key}
                eventKey={key}
                onSelect={(navKey) => {
                  replace(url || pathname);
                  onSelect && onSelect(navKey);
                  !isPhoneWord && setSearchWord('');
                }}
              >
                <div className={prefix('item-label')}>{navTab.label || navTab.key}</div>
              </Nav.Item>
              <span className={prefix('spacing')}>|</span>
            </div>
          );
        })}
      </Nav>
    </div>
  );
};

const SearchBar = ({
  placeholder = '手机号/会员号/姓名精确查询',
  value,
  onChange,
  onSearch,
}: {
  placeholder?: string;
  value?: string;
  onChange?: (value: string) => void;
  onSearch: (value: string) => void;
}) => {
  const { searchWord, setSearchWord } = React.useContext(ToolbarContext);
  const { prefix, classname } = usePrefix('bicester-toolbar-searchbar');

  useEffect(() => {
    setSearchWord(value);
  }, [value]);

  return (
    <div className={classname}>
      <div className={prefix('input-wrap')}>
        <Input
          className={prefix('input')}
          value={searchWord}
          placeholder={placeholder}
          onChange={(value) => {
            onChange && onChange(value);
            setSearchWord(value);
          }}
          onPressEnter={() => onSearch(searchWord)}
        />
      </div>
      <IconButton className="flex items-center" onClick={() => onSearch(searchWord)}>
        <Image src="/resources/images/search-btn.svg" />
      </IconButton>
    </div>
  );
};

BicesterToolbar.NavBar = NavBar;
BicesterToolbar.SearchBar = SearchBar;

export default BicesterToolbar;
